// frontend/src/pages/admin/reports/MonthlyLeaveUsageReport.js
import React, { useMemo, useState } from 'react';
import { Building2, CalendarRange, FileSpreadsheet, Loader2, Search } from 'lucide-react';
import MonthNavigator from '../../../components/UI/MonthNavigator';
import MultiPillSelect from '../../../components/UI/MultiPillSelect';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import PillToggle from '../../../components/UI/PillToggle';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import { LeaveTypeBadge } from '../../../components/UI/LeaveTypeBadge';
import { CountBadge } from '../../../components/UI/CountBadge';
import {
  getDepartmentBadgeColor,
  getDepartmentColor,
  getDepartmentIcon,
  getDepartmentShortName,
} from '../../../config/departmentConfig';
import { useDepartments } from '../../../hooks/useConfigData';
import { usePersistence } from '../../../hooks/usePersistence';
import {
  useExportMonthlyLeaveUsage,
  useMonthlyLeaveUsageReport,
} from '../../../hooks/useMonthlyLeaveUsageReport';
import { formatDate, getTodayInSingaporeTimezone } from '../../../utils/dateUtils';
import { APP_SHELL_STICKY_TOP_CLASS } from '../../../constants/layout';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NO_USAGE_ROW = { leave_type: '', months: Array(12).fill(null), total: null };

const PreviewColgroup = () => (
  <colgroup>
    <col className="w-[20%]" />
    <col className="w-[18%]" />
    {MONTH_ABBR.map((label) => (
      <col key={label} className="w-[4.5%]" />
    ))}
    <col className="w-[8%]" />
  </colgroup>
);

const formatDays = (value) => {
  if (value == null || value === '') return '';
  return Number.isInteger(value) ? String(value) : String(value);
};

const isUsedRow = (row) => row?.total != null;

const employeeMatches = (employee, term) => {
  if (!term) return true;
  const haystack = `${employee.staff_name || ''} ${employee.staff_id || ''}`.toLowerCase();
  return haystack.includes(term);
};

const DepartmentChip = ({ name }) => {
  if (!name) return null;
  const Icon = getDepartmentIcon(name);
  const colors = getDepartmentColor(name);
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${colors.text}`} />
      <InfoBadge
        color={getDepartmentBadgeColor(name)}
        label={getDepartmentShortName(name)}
        title={name}
        size="xs"
      />
    </span>
  );
};

const LeaveTypeLabel = ({ name, used, emptyLabel = '—' }) => {
  if (used && name) {
    return <LeaveTypeBadge leaveTypeName={name} size="xs" />;
  }
  return <span className="text-gray-400">{name || emptyLabel}</span>;
};

const rowsForPreview = (employee, previewMode) => {
  const rows = employee.rows || [];
  if (previewMode === 'all') return rows;
  const taken = rows.filter(isUsedRow);
  return taken.length > 0 ? taken : [NO_USAGE_ROW];
};

const MonthlyLeaveUsageReport = () => {
  const today = getTodayInSingaporeTimezone();
  const currentMonth = (today || '').slice(0, 7);
  const [asOfMonth, setAsOfMonth] = useState(currentMonth);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredStaffId, setHoveredStaffId] = useState(null);
  const [previewMode, setPreviewMode] = usePersistence(
    'monthly-leave-usage-preview-mode',
    'taken',
    ['taken', 'all'],
  );

  const { data: departments = [] } = useDepartments();
  const { data, isLoading, isFetching, isError, error } = useMonthlyLeaveUsageReport(
    asOfMonth,
    departmentIds,
  );
  const exportMutation = useExportMonthlyLeaveUsage();

  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: String(d.id), label: d.department_name })),
    [departments],
  );

  const year = data?.year || Number(asOfMonth.slice(0, 4));
  const cutoffMonth = data?.cutoff_month || Number(asOfMonth.slice(5, 7));
  const monthHeaders = MONTH_ABBR.map((label) => `${label}-${String(year).slice(2)}`);

  const filteredDepartments = useMemo(() => {
    const groups = data?.departments || [];
    const term = searchTerm.trim().toLowerCase();
    return groups
      .map((dept) => ({
        ...dept,
        employees: (dept.employees || [])
          .filter((emp) => employeeMatches(emp, term))
          .map((emp) => ({ ...emp, rows: rowsForPreview(emp, previewMode) })),
      }))
      .filter((dept) => dept.employees.length > 0);
  }, [data, searchTerm, previewMode]);

  const visibleEmployeeCount = useMemo(
    () => filteredDepartments.reduce((sum, dept) => sum + (dept.employees?.length || 0), 0),
    [filteredDepartments],
  );

  const handleExport = () => {
    exportMutation.mutate({ asOfMonth, departmentIds });
  };

  const errorMessage = error?.response?.data?.error || 'Failed to generate report';

  return (
    <div className="space-y-5">
      <div className="min-w-0">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">Monthly Leave Usage</h2>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          Approved leave days by person and type, as of the cutoff. Counts follow the same deduction rules as leave balances: scheduled working days for sick leave and annual leave; calendar days for Home Leave. Download Excel for the full comparable catalogue.
        </p>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={setSearchTerm}
          placeholder="Search by name or staff ID..."
          theme="admin"
          className="w-full lg:max-w-xl"
        />
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <MonthNavigator
            month={asOfMonth}
            onChange={setAsOfMonth}
            theme="admin"
            maxMonth={currentMonth}
            size="compact"
            ariaLabel="As of month"
          />
          <PillToggle
            value={previewMode}
            onChange={setPreviewMode}
            options={[
              { id: 'taken', label: 'Taken' },
              { id: 'all', label: 'All' },
            ]}
            variant="compact"
            theme="admin"
            ariaLabel="Preview rows"
          />
          <MultiPillSelect
            options={departmentOptions}
            value={departmentIds}
            onChange={setDepartmentIds}
            allLabel="All Departments"
            icon={Building2}
            iconColor="text-red-600"
            theme="admin"
          />
          <button
            type="button"
            onClick={handleExport}
            disabled={!data || exportMutation.isPending}
            className="icon-button-admin flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Download Excel"
            aria-label="Download Excel"
          >
            {exportMutation.isPending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {data && (
        <p className="text-xs text-gray-500 flex items-center gap-2">
          <CalendarRange className="w-3.5 h-3.5 text-gray-400" />
          Effective cutoff {formatDate(data.cutoff_date)}
          <span className="text-gray-300">·</span>
          {visibleEmployeeCount} of {data.total_employees} employees
          <span className="text-gray-300">·</span>
          {previewMode === 'taken'
            ? 'Showing types with usage. Excel still includes unused core types.'
            : 'Showing each employee’s full type catalogue.'}
          {isFetching && !isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" /> : null}
        </p>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="h-10 bg-gray-100 animate-pulse" />
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="h-10 bg-white">
                <div className="h-4 mx-4 my-3 bg-gray-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      )}

      {isError && !isLoading && (
        <div className="text-center py-12">
          <CalendarRange className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to generate report</h3>
          <p className="text-sm text-gray-500">{errorMessage}</p>
        </div>
      )}

      {data && !isLoading && visibleEmployeeCount === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No employees match</h3>
          <p className="text-sm text-gray-500">
            {searchTerm
              ? `No employees match “${searchTerm}”.`
              : 'No active employees for the selected cutoff and filters.'}
          </p>
        </div>
      )}

      {data && !isLoading && visibleEmployeeCount > 0 && (
        <>
          <div className="md:hidden space-y-3">
            {filteredDepartments.map((dept) => (
              <div key={dept.department_id || dept.department_name} className="space-y-3">
                {dept.employees.map((emp) => (
                  <div key={emp.staff_id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.staff_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{emp.staff_id}</p>
                      </div>
                      <DepartmentChip name={dept.department_name} />
                    </div>
                    <div className="space-y-3">
                      {emp.rows.map((row, idx) => {
                        const used = isUsedRow(row);
                        return (
                        <div key={`${emp.staff_id}-${row.leave_type || 'blank'}-${idx}`}>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <LeaveTypeLabel
                              name={row.leave_type}
                              used={used}
                              emptyLabel="No leave taken"
                            />
                            <span className={`tabular-nums ${used ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                              {formatDays(row.total) || '—'}
                            </span>
                          </div>
                          {used && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {(row.months || []).map((value, monthIdx) => {
                                if (value == null) return null;
                                const isCutoff = monthIdx + 1 === cutoffMonth;
                                return (
                                  <span
                                    key={`${emp.staff_id}-${row.leave_type}-${monthIdx}`}
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs tabular-nums ${
                                      isCutoff
                                        ? 'bg-amber-50 text-amber-800'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {MONTH_ABBR[monthIdx]} {formatDays(value)}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="hidden md:block">
            {filteredDepartments.map((dept) => (
              <div key={dept.department_id || dept.department_name} className="mb-6 last:mb-0">
                <div className={`sticky ${APP_SHELL_STICKY_TOP_CLASS} z-20 bg-white`}>
                  <div className="flex items-center gap-2 py-2">
                    <DepartmentChip name={dept.department_name} />
                    <CountBadge
                      count={dept.employees.length}
                      theme="admin"
                      size="sm"
                      max={9999}
                    />
                  </div>
                  <table className="w-full table-fixed">
                    <PreviewColgroup />
                    <thead className="table-header">
                      <tr className="bg-gray-50 shadow-[0_1px_0_0_theme(colors.gray.200)]">
                        <th className="table-header-cell-compact">Staff Name</th>
                        <th className="table-header-cell-compact">Leave Type</th>
                        {monthHeaders.map((label, idx) => {
                          const isCutoff = idx + 1 === cutoffMonth;
                          const isFuture = idx + 1 > cutoffMonth;
                          return (
                            <th
                              key={label}
                              className={`table-header-cell-compact text-center ${
                                isCutoff ? 'bg-amber-50 text-amber-800' : ''
                              } ${isFuture ? 'text-gray-400' : ''}`}
                            >
                              {label}
                            </th>
                          );
                        })}
                        <th className="table-header-cell-compact text-right">Total</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <table className="w-full table-fixed divide-y divide-gray-200">
                  <PreviewColgroup />
                  {dept.employees.map((emp, empIdx) => {
                    const staffRows = emp.rows || [];
                    return (
                      <tbody
                        key={emp.staff_id}
                        className={`table-body table-row-group${hoveredStaffId === emp.staff_id ? ' is-hovered' : ''}`}
                        onMouseEnter={() => setHoveredStaffId(emp.staff_id)}
                        onMouseLeave={() => setHoveredStaffId(null)}
                      >
                        {staffRows.map((row, rowIdx) => {
                        const used = isUsedRow(row);
                        return (
                          <tr
                            key={`${emp.staff_id}-${row.leave_type || 'blank'}-${rowIdx}`}
                            className={`table-row-dense ${
                              empIdx === 0 && rowIdx === 0
                                ? ''
                                : rowIdx === 0
                                  ? 'border-t border-gray-100'
                                  : ''
                            }`}
                          >
                            {rowIdx === 0 && (
                              <td
                                rowSpan={staffRows.length}
                                className="table-cell-compact align-top"
                              >
                                <div className="font-medium text-gray-900 truncate" title={emp.staff_name}>
                                  {emp.staff_name}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">{emp.staff_id}</div>
                              </td>
                            )}
                            <td className="table-cell-compact whitespace-normal">
                              <LeaveTypeLabel name={row.leave_type} used={used} />
                            </td>
                            {(row.months || []).map((value, monthIdx) => (
                              <td
                                key={`${emp.staff_id}-${row.leave_type}-${monthIdx}`}
                                className={`table-cell-compact text-center tabular-nums ${
                                  monthIdx + 1 === cutoffMonth ? 'bg-amber-50/60' : ''
                                } ${
                                  monthIdx + 1 > cutoffMonth
                                    ? 'bg-gray-50 text-gray-300'
                                    : used
                                      ? ''
                                      : 'text-gray-300'
                                }`}
                              >
                                {formatDays(value)}
                              </td>
                            ))}
                            <td className={`table-cell-compact text-right tabular-nums ${
                              used ? 'font-medium text-gray-900' : 'text-gray-400'
                            }`}>
                              {formatDays(row.total)}
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    );
                  })}
                </table>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyLeaveUsageReport;
